import React from "react"
import { rem } from "polished"
import { graphql } from "gatsby"
import styled from "styled-components"
import { media } from "@sr/common"
import { useSanity } from "@sr/common"
import { Page, Seo, SectionIntroductionVideo } from "@sr/gatsby-theme-ui"
import Layout from "../components/Layout"
import CaseStudyEntryHero from "../components/Section/CaseStudyEntry/CaseStudyEntryHero"
import CaseStudyEntryCta from "../components/Section/CaseStudyEntry/CaseStudyEntryCta"
import CaseStudyEntryRelated from "../components/Section/CaseStudyEntry/CaseStudyEntryRelated"
import CaseStudyEntrySidebar from "../components/Section/CaseStudyEntry/CaseStudyEntrySidebar"
import CaseStudyEntryContent from "../components/Section/CaseStudyEntry/CaseStudyEntryContent"

const templateCaseStudyEntry = ({ data }: any) => {
  const { intro, quote, sidebar, cta, _rawBlock, title, video } =
    data.sanityCaseStudyEntry
  const buttonLink = cta.buttonLink?.reference?.path?.current
  const relatedEntries = data.allSanityCaseStudyEntry.edges

  return (
    <Layout>
      <Page>
        <TemplateCaseStudyEntryStyled>
          <CaseStudyEntryHero intro={intro} title={title} />
          {video?.displayVideo ? (
            <SectionIntroductionVideo
              videoTitle={video.title}
              videoUrl={video.url}
            />
          ) : null}
          <section className="case-study-entry__main-container">
            <CaseStudyEntrySidebar sidebar={sidebar} />
            <CaseStudyEntryContent contentData={_rawBlock} quote={quote} />
          </section>
          <CaseStudyEntryRelated relatedEntries={relatedEntries} />
          <footer>
            <CaseStudyEntryCta
              text={cta.title}
              btnText={cta.buttonText}
              btnLink={buttonLink}
            />
          </footer>
        </TemplateCaseStudyEntryStyled>
      </Page>
    </Layout>
  )
}

export default templateCaseStudyEntry

export const Head = ({ data }: any) => {
  const { imgBuilder } = useSanity()
  const sanitySeoData = data.sanityCaseStudyEntry.seo
  let metaArray = []

  if (data?.sanityPage?.ogImage != undefined) {
    const src = imgBuilder(data?.sanityPage?.ogImage?.asset)
      .width(1200)
      .height(627)
      .auto("format")
      .crop("focalpoint")
      .url()

    metaArray.push({
      property: "og:image",
      content: src,
    })
  }

  return sanitySeoData ? (
    <Seo
      title={sanitySeoData?.seoTools?.seo_title || ""}
      noIndex={sanitySeoData?.noindex || false}
      canonical={sanitySeoData?.canonical || ""}
      keywords={sanitySeoData?.seoTools?.focus_keyword || ""}
      description={sanitySeoData?.seoTools?.meta_description || ""}
      meta={metaArray.length ? metaArray : []}
    />
  ) : null
}

const TemplateCaseStudyEntryStyled = styled.article`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  footer {
    width: 100%;
  }

  .case-study-entry__main-container {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;

    @media ${media.stMidUp} {
      flex-direction: row;
      align-items: flex-start;
      max-width: ${rem(1232)};
    }
  }
`

export const CaseStudyEntryQuery = graphql`
  query CaseStudyEntryQuery($path_context: String!) {
    sanityCaseStudyEntry(path: { current: { eq: $path_context } }) {
      _rawBlock(resolveReferences: { maxDepth: 10 })
      path {
        current
      }
      title
      quote {
        quoteText
        quoteCitation
        quoteImg {
          alt
          ...SanityImageWithPreview
        }
      }
      seo {
        title
        canonical
        noindex
        seoTools {
          focus_keyword
          focus_synonyms
          meta_description
          seo_title
        }
      }
      ogImage {
        asset {
          _id
        }
      }
      sidebar {
        company
        industry
        headquarters
        productsUsed
      }
      intro {
        customerName
        snippet
        intro
        customerLogo {
          alt
          ...SanityImageWithPreview
        }
        heroImg {
          alt
          ...SanityImageWithPreview
        }
        caseStudyEntryKpisList {
          label
          value
        }
      }
      video {
        displayVideo
        title
        url
      }
      cta {
        title
        buttonText
        buttonLink {
          ...SanityBuilderInternalLink
        }
      }
    }

    allSanityCaseStudyEntry(
      limit: 3
      filter: { path: { current: { ne: $path_context } } }
      sort: { _createdAt: DESC }
    ) {
      edges {
        node {
          path {
            current
          }
          sidebar {
            company
            headquarters
            industry
            productsUsed
          }
          intro {
            intro
            snippet
            customerLogo {
              alt
              ...SanityImageWithPreview
            }
          }
        }
      }
    }
  }
`
